import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  Box,
  CircularProgress,
  Alert,
  TablePagination
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

import { 
  getAllDiscountCodes,
  deleteDiscountCode,
  DiscountCode
} from '../../../services/discountCodeService';

interface DiscountCodeListProps {
  onEdit: (discountCode: DiscountCode) => void;
  key?: string; // Adding an optional key prop to force re-render
}

function DiscountCodeList({ onEdit }: DiscountCodeListProps) {
  const [discountCodes, setDiscountCodes] = useState<DiscountCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Fetch discount codes when component mounts
  useEffect(() => {
    fetchDiscountCodes();
  }, []);
  
  // Fetch all discount codes
  const fetchDiscountCodes = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const codes = await getAllDiscountCodes(false);
      setDiscountCodes(codes);
    } catch (error) {
      console.error('Error fetching discount codes:', error);
      setError('Failed to load discount codes. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle delete discount code
  const handleDeleteCode = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this discount code?')) {
      return;
    }
    
    try {
      await deleteDiscountCode(id);
      fetchDiscountCodes();
    } catch (error) {
      console.error('Error deleting discount code:', error);
      setError('Failed to delete discount code. Please try again.');
    }
  };
  
  // Handle pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Format discount value
  const formatDiscount = (code: DiscountCode) => {
    if (code.discountType === 'percentage') {
      return `${code.discountValue}%`;
    } else {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(code.discountValue);
    }
  };
  
  // Check if discount code is expired
  const isExpired = (code: DiscountCode) => {
    if (!code.expiresAt) return false;
    return new Date(code.expiresAt) < new Date();
  };
  
  // Check if discount code has reached max usage
  const isMaxedOut = (code: DiscountCode) => {
    if (code.maxUses === null) return false;
    return code.usedCount >= code.maxUses;
  };
  
  // Render discount code status
  const renderStatus = (code: DiscountCode) => {
    if (!code.isActive) {
      return <Chip size="small" label="Inactive" color="default" icon={<BlockIcon />} />;
    }
    
    if (isExpired(code)) {
      return <Chip size="small" label="Expired" color="error" />;
    }
    
    if (isMaxedOut(code)) {
      return <Chip size="small" label="Maxed Out" color="warning" />;
    }
    
    return <Chip size="small" label="Active" color="success" icon={<CheckCircleIcon />} />;
  };
  
  return (
    <Paper elevation={2}>
      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Discount</TableCell>
                  <TableCell>Usage</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {discountCodes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body1" sx={{ py: 3 }}>
                        No discount codes found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  discountCodes
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((code) => (
                      <TableRow key={code.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                            {code.code}
                          </Typography>
                        </TableCell>
                        <TableCell>{formatDiscount(code)}</TableCell>
                        <TableCell>
                          {code.usedCount} {code.maxUses !== null ? `/ ${code.maxUses}` : ''}
                        </TableCell>
                        <TableCell>
                          {code.expiresAt ? new Date(code.expiresAt).toLocaleDateString() : 'Never'}
                        </TableCell>
                        <TableCell>{renderStatus(code)}</TableCell>
                        <TableCell>{code.description}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit">
                            <IconButton 
                              size="small" 
                              onClick={() => onEdit(code)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton 
                              size="small" 
                              onClick={() => handleDeleteCode(code.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={discountCodes.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}
    </Paper>
  );
}

export default DiscountCodeList;